import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  InvitationEmailPreview,
  type InvitationEmailPreviewEvent,
  type InvitationEmailVariant,
} from "./InvitationEmailPreview";

export interface InvitationEmailPreviewRecipient {
  /** Roster-style "Last, First" name for the dialog header. */
  displayName: string;
  /** Name the email greets them by (stored display name), or null. */
  greetingName: string | null;
  email: string;
  /** Per-event code for new users; null for active users. */
  code: string | null;
  /** Active users get the "You've Been Added" variant; new users get the code variant. */
  isActive: boolean;
}

interface InvitationEmailPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  event: InvitationEmailPreviewEvent | null;
  recipient: InvitationEmailPreviewRecipient | null;
}

const VARIANT_LABEL: Record<InvitationEmailVariant, string> = {
  invitation: "New user",
  added: "Existing user",
};

/**
 * Variant switch. When `locked` is given, only that variant can be chosen;
 * the other stays visible but disabled so the organizer can see both kinds
 * exist without being able to preview one the person would never get.
 */
export function EmailVariantToggle({
  value,
  onChange,
  locked,
}: {
  value: InvitationEmailVariant;
  onChange: (v: InvitationEmailVariant) => void;
  locked?: InvitationEmailVariant;
}) {
  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v: InvitationEmailVariant | null) => v && onChange(v)}
      aria-label="Email variant"
    >
      {(["invitation", "added"] as InvitationEmailVariant[]).map((v) => {
        const disabled = locked !== undefined && locked !== v;
        const button = (
          <ToggleButton
            key={v}
            value={v}
            disabled={disabled}
            data-testid={`email-variant-${v}`}
          >
            {VARIANT_LABEL[v]}
          </ToggleButton>
        );
        return disabled ? (
          <Tooltip key={v} title={`This person will be sent the ${VARIANT_LABEL[locked]} email.`}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        );
      })}
    </ToggleButtonGroup>
  );
}

export function InvitationEmailPreviewDialog({
  open,
  onClose,
  event,
  recipient,
}: InvitationEmailPreviewDialogProps) {
  const locked: InvitationEmailVariant = recipient?.isActive ? "added" : "invitation";
  const [variant, setVariant] = useState<InvitationEmailVariant>(locked);

  useEffect(() => {
    if (open) setVariant(locked);
  }, [open, locked]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="invitation-email-preview-dialog">
      <DialogTitle>Email preview</DialogTitle>
      <DialogContent>
        {event && recipient ? (
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
              <Typography variant="body2" color="text.secondary">
                To: <strong>{recipient.displayName}</strong> &lt;{recipient.email}&gt;
              </Typography>
              <EmailVariantToggle value={variant} onChange={setVariant} locked={locked} />
            </Stack>
            <Typography variant="body2" color="text.secondary" data-testid="invitation-email-preview-note">
              {recipient.isActive
                ? "This person is an existing user and will be sent the Existing user email. This is exactly what goes out."
                : "This person is a new user and will be sent the New user email with their real reservation code. This is exactly what goes out."}
            </Typography>
            <InvitationEmailPreview
              event={event}
              recipientName={recipient.greetingName}
              recipientEmail={recipient.email}
              invitationCode={recipient.code}
              variant={variant}
              height={520}
            />
          </Stack>
        ) : (
          <Typography color="text.secondary">Nothing to preview.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
